import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { ChevronDown, Eye, Loader2, Paperclip, Upload } from "lucide-react";
import { useDocPreview } from "./DocPreview";
import { BrandPreview } from "./BrandPreview";
import { adultCutoff } from "./associateSpec";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const Err = ({ e }) => (e ? <p className="mt-1 text-xs text-red-600">{e}</p> : null);
const selCls = "h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-600";
const inCls = "h-9 border-gray-300 text-sm focus-visible:ring-teal-500 disabled:bg-gray-50 disabled:text-gray-700 disabled:opacity-100";
const lblCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500";
const Lbl = ({ children, req }) => <label className={lblCls}>{children} {req && <span className="text-rose-500">*</span>}</label>;
const tid = (k) => `assoc-field-${k.replace(/_/g, "-")}`;

export const FileField = ({ fl, value, onChange, ro: roProp, ctx, compact }) => {
  const ro = roProp || fl.readOnly;
  const [busy, setBusy] = useState(false);
  const pv = useDocPreview();
  const open = () => {
    const url = ctx.token ? `${ctx.publicBase || `${API}/associate-verify/${ctx.token}`}/files/${value.storage_path}` : `${fl.fileBase || ctx.base || `${API}/associates/${ctx.id}`}/files/${value.storage_path}`;
    pv.show(url, ctx.token ? {} : authHdr(), value.name || fl.label);
  };
  const up = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|pdf)$/i.test(file.name)) return toast.error("Only PNG, JPG or PDF allowed");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${ctx.base || `${API}/associates/${ctx.id}`}/files?slot=${fl.key}`, fd, { headers: authHdr() });
      onChange(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const attached = !!value?.storage_path;
  const pill = "inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition-colors";
  const chips = (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {attached && (
        <button type="button" onClick={open} data-testid={`${tid(fl.key)}-view`} title={value.name}
          className={`${pill} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
          <Eye className="h-3 w-3 shrink-0" /><span className="truncate">View scan{value.name ? ` · ${value.name}` : ""}</span>
        </button>
      )}
      {ro && !attached && <span className={`${pill} border-dashed border-gray-200 text-gray-400`} data-testid={`${tid(fl.key)}-empty`}><Paperclip className="h-3 w-3" />No scan uploaded</span>}
      {!ro && (
        <label className={`${pill} cursor-pointer border-gray-300 bg-white text-gray-600 hover:bg-gray-50 ${busy ? "pointer-events-none opacity-60" : ""}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {busy ? "Uploading…" : attached ? "Replace" : "Upload scan"}
          <input type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden" data-testid={tid(fl.key)} onChange={(e) => up(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
  return (
    <div>
      {pv.dialog}
      {!compact && (
        <button type="button" disabled={!attached} onClick={open} data-testid={`${tid(fl.key)}-box`}
          className={`flex h-9 w-full items-center truncate rounded-md border border-dashed px-3 text-left text-sm transition-colors ${attached ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50/60" : "border-gray-300 text-gray-400"}`}>
          <span className="truncate">{attached ? value.name || fl.label : fl.label.charAt(0) + fl.label.slice(1).toLowerCase()}</span>
        </button>
      )}
      {chips}
    </div>
  );
};

export const AddressBlock = ({ data: raw, set: rawSet, ro, errs: rawErrs, prefix = "" }) => {
  // `prefix` lets one step hold a second address (e.g. guardian_country, guardian_city…).
  const strip = (o) => Object.fromEntries(Object.entries(o || {}).filter(([k]) => k.startsWith(prefix)).map(([k, v]) => [k.slice(prefix.length), v]));
  const data = prefix ? strip(raw) : raw;
  const errs = prefix ? strip(rawErrs) : rawErrs;
  const set = prefix ? (p) => rawSet(Object.fromEntries(Object.entries(p).map(([k, v]) => [prefix + k, v]))) : rawSet;
  const tidA = (k) => tid(prefix + k);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [pins, setPins] = useState([]);
  const L = `${API}/lead-mgmt/lookups`;
  const get = (u) => axios.get(u, { headers: authHdr() }).then((r) => r.data?.data || []).catch(() => []);
  useEffect(() => { if (!ro) get(`${L}/countries`).then(setCountries); }, [ro]); // eslint-disable-line
  useEffect(() => { if (!ro && data.country) get(`${L}/states?country=${encodeURIComponent(data.country)}`).then(setStates); }, [data.country, ro]); // eslint-disable-line
  useEffect(() => { if (!ro && data.country && data.state) get(`${L}/cities?country=${encodeURIComponent(data.country)}&state=${encodeURIComponent(data.state)}`).then(setCities); }, [data.state, ro]); // eslint-disable-line
  const searchPin = async (q) => {
    set({ pincode: q });
    if (q.length >= 3) setPins(await get(`${L}/pincodes?country=${encodeURIComponent(data.country)}&q=${q}`));
  };
  const pickPin = (p) => { set({ pincode: p.pin, state: p.state, city: p.city }); setPins([]); };
  const onCountry = (name) => {
    const c = countries.find((x) => x.name === name);
    set({ country: name, has_pincodes: !!c?.has_pincodes, pincode: "", state: "", city: "" });
  };
  if (ro) return (
    <div className="grid gap-4 sm:grid-cols-2 md:col-span-3 md:grid-cols-4">
      {["country", "pincode", "state", "city"].map((k) => (
        <div key={k}><Lbl>{k}</Lbl><Input className={inCls} value={data[k] || ""} disabled data-testid={tidA(k)} /></div>
      ))}
    </div>
  );
  return (
    <div className="grid gap-4 sm:grid-cols-2 md:col-span-3 md:grid-cols-4">
      <div><Lbl req>Country</Lbl>
        <select className={selCls} value={data.country || ""} onChange={(e) => onCountry(e.target.value)} data-testid={tidA("country")}>
          <option value="">Select country</option>{countries.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select><Err e={errs.country} /></div>
      <div className="relative"><Lbl req={data.has_pincodes}>Pincode</Lbl>
        <Input className={inCls} value={data.has_pincodes ? data.pincode || "" : ""} disabled={!data.has_pincodes} placeholder={data.country && !data.has_pincodes ? "Not applicable for this country" : "Search pincode"}
          onChange={(e) => searchPin(e.target.value)} data-testid={tidA("pincode")} />
        {pins.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-white shadow">
            {pins.map((p) => <button type="button" key={p.pin + p.city} onClick={() => pickPin(p)} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50">{p.pin} · {p.city}, {p.state}</button>)}
          </div>
        )}<Err e={errs.pincode} /></div>
      <div><Lbl req>State</Lbl>
        {states.length ? (
          <select className={selCls} value={data.state || ""} onChange={(e) => set({ state: e.target.value, city: "" })} data-testid={tidA("state")}>
            <option value="">Select state</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ): <Input className={inCls} value={data.state || ""} onChange={(e) => set({ state: e.target.value })} data-testid={tidA("state")} />}<Err e={errs.state} /></div>
      <div><Lbl req>City</Lbl>
        <select className={selCls} value={data.city || ""} disabled={!data.state} onChange={(e) => set({ city: e.target.value })} data-testid={tidA("city")}>
          <option value="">Select city</option>
          {[...new Set([...(data.city ? [data.city] : []), ...cities])].map((c) => <option key={c} value={c}>{c}</option>)}
        </select><Err e={errs.city} /></div>
    </div>
  );
};

const BpsPct = ({ v = {}, onChange, ro }) => {
  const b = Number(v.bps || 0) > 0, p = Number(v.pct || 0) > 0;
  return (
    <div className="flex gap-2">
      <Input className={inCls} type="number" step="any" min="0" placeholder="BPS" value={v.bps ?? ""} disabled={ro || p}
        onChange={(e) => onChange({ bps: e.target.value, pct: Number(e.target.value) > 0 ? 0 : v.pct })} />
      <Input className={inCls} type="number" step="any" min="0" placeholder="%" value={v.pct ?? ""} disabled={ro || b}
        onChange={(e) => onChange({ pct: e.target.value, bps: Number(e.target.value) > 0 ? 0 : v.bps })} />
    </div>
  );
};

const splitList = (v) => String(v || "").split(",").map((x) => x.trim()).filter(Boolean);

const MultiPick = ({ fl, v, dis, bad, set }) => {
  const cur = splitList(v);
  const pick = (o, c) => {
    const nx = c ? [...cur, o] : cur.filter((x) => x !== o);
    set({ [fl.key]: nx.join(", "), ...(fl.patch ? fl.patch(nx) : {}) });
  };
  return (
    <Popover>
      <PopoverTrigger asChild disabled={dis}>
        <button type="button" className={selCls + bad + " flex items-center justify-between text-left disabled:bg-gray-50"} data-testid={tid(fl.key)}>
          <span className="truncate">{cur.length ? cur.join(", ") : <span className="text-gray-400">{fl.options.length ? "Select one or more" : fl.empty || "Select"}</span>}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1" data-testid={`${tid(fl.key)}-menu`}>
        {fl.options.length === 0 && <p className="px-2 py-1.5 text-sm text-gray-500">{fl.empty || "No options"}</p>}
        {fl.options.map((o) => (
          <label key={o} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50" data-testid={`${tid(fl.key)}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <Checkbox checked={cur.includes(o)} onCheckedChange={(c) => pick(o, !!c)} />{o}
          </label>))}
      </PopoverContent>
    </Popover>);
};

export const Field = ({ fl, data, set, ro, errs, ctx, attach }) => {
  const v = fl.compute ? fl.compute() : data[fl.key];
  const dis = ro || fl.readOnly;
  const upper = ["pan", "ifsc", "gst", "tan", "euin"].includes(fl.type);
  if (fl.type === "header") return <h5 className="mt-2 border-b border-gray-100 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-600 md:col-span-3">{fl.label}</h5>;
  if (fl.type === "address") return <AddressBlock data={data} set={set} ro={ro} errs={errs} prefix={fl.prefix} />;
  if (fl.type === "brandpreview") return <BrandPreview data={data} ctx={ctx} />;
  const label = <Lbl req={fl.req && !fl.readOnly && !ro}>{fl.label}</Lbl>;
  const bad = errs[fl.key] ? " border-rose-400" : "";
  let input;
  if (fl.type === "select") input = (
    <select className={selCls + bad} value={v || ""} disabled={dis} onChange={(e) => set({ [fl.key]: e.target.value, ...(fl.clears || {}) })} data-testid={tid(fl.key)}>
      <option value="">Select</option>{fl.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>);
  else if (fl.type === "multipick") input = <MultiPick fl={fl} v={v} dis={dis} bad={bad} set={set} />;
  else if (fl.type === "file") input = <FileField fl={fl} value={v} ro={ro} ctx={ctx} onChange={(m) => set({ [fl.key]: m })} />;
  else if (fl.type === "checkbox") input = (
    <div className="flex h-9 items-center"><Checkbox checked={!!v} disabled={dis} onCheckedChange={(c) => set({ [fl.key]: !!c })} data-testid={tid(fl.key)} /></div>);
  else if (fl.type === "multi") input = (
    <div className="flex h-9 items-center gap-4" data-testid={tid(fl.key)}>{fl.options.map((o) => (
      <label key={o} className="flex items-center gap-1.5 text-sm"><Checkbox checked={(v || []).includes(o)} disabled={dis}
        onCheckedChange={(c) => set({ [fl.key]: c ? [...(v || []), o] : (v || []).filter((x) => x !== o) })} />{o}</label>))}</div>);
  else if (fl.type === "color") {
    const ok = /^#[0-9A-Fa-f]{6}$/.test(v || "");
    input = (
      <div className={`flex h-9 items-center gap-2 overflow-hidden rounded-md border pr-2 ${errs[fl.key] ? "border-rose-400" : "border-gray-300"} ${dis ? "bg-gray-50" : "bg-white"}`}>
        <label className={`relative ml-1.5 h-6 w-6 shrink-0 rounded border border-gray-300 ${dis ? "" : "cursor-pointer"}`}
          style={{ background: ok ? v : "repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 50% / 8px 8px" }} data-testid={`${tid(fl.key)}-preview`} title="Pick a colour">
          <input type="color" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" value={ok ? v : "#000000"} disabled={dis}
            data-testid={`${tid(fl.key)}-picker`} onChange={(e) => set({ [fl.key]: e.target.value.toUpperCase() })} />
        </label>
        <input className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-700 shadow-none outline-none ring-0 focus:outline-none focus:ring-0 disabled:text-gray-700" value={v || ""} disabled={dis}
          placeholder={dis ? "" : "#1A2B3C"} maxLength={7} data-testid={tid(fl.key)}
          onChange={(e) => { let nv = e.target.value.toUpperCase().replace(/[^#0-9A-F]/g, ""); if (nv && !nv.startsWith("#")) nv = `#${nv}`; set({ [fl.key]: nv }); }} />
      </div>);
  }
  else if (fl.type === "bpspct") input = <div data-testid={tid(fl.key)}><BpsPct v={v} ro={dis} onChange={(nv) => set({ [fl.key]: nv })} /></div>;
  else if (fl.type === "arn" || fl.type === "euin") {
    const pre = fl.type === "arn" ? "ARN-" : "E";
    const strip = (x) => String(x || "").toUpperCase().replace(fl.type === "arn" ? /^ARN-?/ : /^E/, "");
    input = (
      <div className={`flex h-9 overflow-hidden rounded-md border ${errs[fl.key] ? "border-rose-400" : "border-gray-300"} ${dis ? "bg-gray-50" : "bg-white"}`}>
        <span className="flex items-center border-r border-gray-300 bg-gray-100 px-2.5 text-sm font-medium text-gray-600" data-testid={`${tid(fl.key)}-prefix`}>{pre}</span>
        <input className="min-w-0 flex-1 bg-transparent px-3 text-sm text-gray-700 outline-none disabled:text-gray-700" value={strip(v)} disabled={dis}
          data-testid={tid(fl.key)} onChange={(e) => set({ [fl.key]: strip(e.target.value) })} />
      </div>);
  }
  else input = (
    <Input className={inCls + bad} type={fl.type === "date" ? "date" : "text"} value={v || ""} disabled={dis}
      max={fl.minAge ? adultCutoff(fl.minAge) : undefined}
      placeholder={fl.type === "color" && !dis ? "#1A2B3C" : ""} data-testid={tid(fl.key)}
      onChange={(e) => {
        const nv = upper ? e.target.value.toUpperCase() : e.target.value;
        set({ [fl.key]: nv });
        if (fl.type === "ifsc" && nv.length === 11) ctx.onIfsc?.(fl.key, nv);
      }} />);
  return (
    <div>{label}{input}<Err e={errs[fl.key]} />
      {attach && <><FileField fl={attach} value={data[attach.key]} ro={ro} ctx={ctx} compact onChange={(m) => set({ [attach.key]: m })} /><Err e={errs[attach.key]} /></>}
    </div>
  );
};

const HOSTS = ["text", "pan", "aadhaar", "ifsc", "gst", "tan", "arn", "euin", "email", "mobile", "account", "phone", "date", "select"];
// Pairs each document with the input right before it so "Upload scan" sits under its number field.
export const pairFields = (fields) => {
  const out = [];
  fields.forEach((fl) => {
    const prev = out[out.length - 1];
    if (fl.type === "file" && !fl.solo && prev && !prev.attach && HOSTS.includes(prev.fl.type)) prev.attach = fl;
    else out.push({ fl });
  });
  return out;
};
